import { Button, Form, Select, Steps } from "antd";
import { toast } from "react-toastify";
import { useForm } from "antd/es/form/Form";
import { useState } from "react";

import Typography from "../../Typography/Typography";
import { queryClient } from "@/common/utils";
import { usePermissions } from "@/common/contexts/Permissions.context";
import { useModal } from "@/common/contexts/Modal.context";
import { DocumentFormFields } from "@/common/types/forms/DocumentFormFields.type";
import useUploadDocument from "@/common/api/mutation/documentsMutations/useUploadDocument.mutation";
import DocumentAgreementStep from "./DocumentAgreementStep";
import DocumentProductStep from "./DocumentProductStep";
import DocumentOtherStep from "./DocumentOtherStep";
import { documentTypes } from "@/common/const";

const steps = [
  {
    title: "Step",
  },
  {
    title: "Step",
  },
];

export default function UploadDocumentModal() {
  const { closeModal } = useModal();
  const [currentStep, setCurrentStep] = useState(0);
  const { company } = usePermissions();
  const { mutate } = useUploadDocument();

  const onFinish = (formValues: DocumentFormFields) => {
    mutate(
      {
        formData: { ...formValues },
        companyId: company.id,
        documentType: form.getFieldValue("type"),
      },
      {
        onSuccess: () => {
          closeModal();
          queryClient.invalidateQueries();
          toast.success("Document added successfully!");
        },
      }
    );
  };
  const [form] = useForm<DocumentFormFields>();

  const items = steps.map((item) => ({ key: item.title, title: item.title }));

  return (
    <div className="flex flex-col items-center gap-2">
      <Typography
        mb={4}
        type="p"
        fontFamily="font-kanit"
        fontSize={24}
        textAlign="center"
        fontWeight="font-medium"
        uppercase
      >
        Upload Document
      </Typography>
      <div className="flex flex-col gap-4">
        <Steps current={currentStep} items={items} />
        <Form form={form} onFinish={onFinish}>
          <div className={currentStep === 0 ? "block" : "hidden"}>
            <div className="flex items-center gap-2">
              <div>
                <Typography
                  mb={4}
                  type="p"
                  fontFamily="font-kanit"
                  fontWeight="font-light"
                  fontSize={16}
                >
                  Select document type
                  <Typography
                    type="span"
                    fontFamily="font-kanit"
                    fontWeight="font-light"
                    textColor="error"
                  >
                    *
                  </Typography>
                </Typography>

                <Form.Item
                  name="type"
                  rules={[{ required: true, message: "Please select a type" }]}
                >
                  <Select
                    placeholder="Choose type"
                    className="min-w-[230px] w-full bg-gray-border hover:bg-primaryDim h-10 rounded-lg "
                  >
                    <Select.Option value="agreement">Agreement</Select.Option>
                    <Select.Option value="product">
                      Product description
                    </Select.Option>
                    <Select.Option value="other">Other Documents</Select.Option>
                  </Select>
                </Form.Item>
              </div>
              <div>
                <Typography
                  mb={4}
                  type="p"
                  fontFamily="font-kanit"
                  fontWeight="font-light"
                  fontSize={16}
                >
                  User type
                  <Typography
                    type="span"
                    fontFamily="font-kanit"
                    fontWeight="font-light"
                    textColor="error"
                  >
                    *
                  </Typography>
                </Typography>
                <Form.Item
                  name="userType"
                  rules={[
                    { required: true, message: "Please select a user type" },
                  ]}
                >
                  <Select
                    placeholder="Choose user type"
                    className="min-w-[230px] bg-gray-border hover:bg-primaryDim h-10 rounded-lg"
                  >
                    {company.companyType === "airx" ? (
                      <>
                        <Select.Option value="customer">Customer</Select.Option>
                        <Select.Option value="vendor">Vendor</Select.Option>
                        <Select.Option value="airx">AIRX</Select.Option>
                      </>
                    ) : company.companyType === "vendor" ? (
                      <Select.Option value="vendor">Vendor</Select.Option>
                    ) : (
                      <Select.Option value="customer">Customer</Select.Option>
                    )}
                  </Select>
                </Form.Item>
              </div>
            </div>
            <div className="flex items-center justify-center mt-4">
              <Button
                onClick={() => {
                  form.validateFields(["type", "userType"]).then(() => {
                    setCurrentStep((state) => state + 1);
                  });
                }}
                type="primary"
                className="min-w-[253px] h-10 text-base font-inter font-bold px-20"
              >
                Next
              </Button>
            </div>
          </div>
          <div className={currentStep === 1 ? "block" : "hidden"}>
            {form.getFieldValue("type") === documentTypes[0] ? (
              <DocumentAgreementStep
                selectedCompanyType={form.getFieldValue("userType")}
                form={form}
              />
            ) : form.getFieldValue("type") === documentTypes[1] ? (
              <DocumentProductStep
                selectedCompanyType={form.getFieldValue("userType")}
                form={form}
              />
            ) : (
              <DocumentOtherStep
                selectedCompanyType={form.getFieldValue("userType")}
                form={form}
              />
            )}
            <div className="flex w-full items-center justify-between mt-4">
              <Button
                onClick={() => setCurrentStep((state) => state - 1)}
                type="default"
                className="max-w-[118px] h-10 text-base font-inter font-bold px-20"
              >
                Back
              </Button>
              <Button
                htmlType="submit"
                type="primary"
                className="min-w-[280px] h-10 text-base font-inter font-bold px-20"
              >
                Upload
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
}
